import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { AdminLayout } from "@/pages/AdminDashboard";
import api from "@/services/api";
import { toast } from "sonner";
import {
  UserPlus,
  Trash2,
  Shield,
  User,
  Eye
} from "lucide-react";

const ROLES = [
  { value: "super_admin", label: "Super Admin", icon: Shield, description: "Full access to all features" },
  { value: "planner", label: "Planner", icon: User, description: "View submissions, update status, add notes" },
  { value: "viewer", label: "Viewer", icon: Eye, description: "View-only access" }
];

export default function AdminUsers() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "viewer"
  });
  const [creating, setCreating] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.getUsers(token);
      if (!response.error) {
        setUsers(response.users || []);
      } else {
        toast.error("Failed to fetch users");
      }
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await api.createUser(newUser, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setUsers([...users, response.user]);
        setShowModal(false);
        setNewUser({ name: "", email: "", password: "", role: "viewer" });
        toast.success("User created successfully");
      }
    } catch (error) {
      toast.error("Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      const response = await api.deleteUser(userId, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setUsers(users.filter((u) => u.id !== userId));
        toast.success("User deleted");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const getRoleInfo = (role) => ROLES.find((r) => r.value === role) || ROLES[2];

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="admin-users">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-white">User Management</h1>
            <p className="text-gray-400 mt-1">Manage admin panel access</p>
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors"
            data-testid="btn-add-user"
          >
            <UserPlus className="w-5 h-5" />
            Add User
          </button>
        </div>

        {/* Users List */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {users.map((user) => {
                const roleInfo = getRoleInfo(user.role);
                const RoleIcon = roleInfo.icon;

                return (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#c4646a]/20 flex items-center justify-center">
                        <span className="text-[#c4646a] font-medium text-lg">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2 text-gray-400">
                        <RoleIcon className="w-4 h-4" />
                        <span className="text-sm capitalize">
                          {user.role?.replace("_", " ")}
                        </span>
                      </div>

                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          data-testid={`delete-${user.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Role Descriptions */}
        <div className="mt-8">
          <h2 className="text-lg font-medium text-white mb-4">Role Permissions</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {ROLES.map((role) => {
              const RoleIcon = role.icon;
              return (
                <div
                  key={role.value}
                  className="bg-white/5 border border-white/10 rounded-xl p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#c4646a]/20 flex items-center justify-center">
                      <RoleIcon className="w-5 h-5 text-[#c4646a]" />
                    </div>
                    <span className="text-white font-medium">{role.label}</span>
                  </div>
                  <p className="text-gray-400 text-sm">{role.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6">
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-medium text-white">Add New User</h2>
            </div>

            <form onSubmit={createUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="luxury-input"
                  placeholder="John Doe"
                  required
                  data-testid="input-name"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="luxury-input"
                  placeholder="john@inventivepages.com"
                  required
                  data-testid="input-email"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="luxury-input"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="input-password"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="luxury-input"
                  data-testid="select-role"
                >
                  {ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-3 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors disabled:opacity-50"
                  data-testid="btn-create-user"
                >
                  {creating ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
